import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getEmail, getBlobEmail, forwardEmail, createOutlookDraft, downloadAttachment } from '../api';
import { useGraphAuth } from '../hooks/useGraphAuth';
import { getFromCache, addToCache } from '../utils/emailCache';

function EmailDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHtml, setShowHtml] = useState(true);
  const [forwarding, setForwarding] = useState(false);
  const [forwardMode, setForwardMode] = useState('outlook'); // 'outlook' or 'eml'
  const [forwardError, setForwardError] = useState(null);
  const [forwardSuccess, setForwardSuccess] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);

  // Track blob info for blob-based emails
  const blobPath = searchParams.get('blobPath');
  const blobContainer = searchParams.get('container');
  const blobInfo = blobPath ? { container: blobContainer, path: blobPath } : null;

  // Azure AD authentication hook
  const { isConfigured: isAzureConfigured, getAccessToken } = useGraphAuth();

  // Auto-select EML mode if Azure AD not configured
  useEffect(() => {
    if (!isAzureConfigured && forwardMode === 'outlook') {
      setForwardMode('eml');
    }
  }, [isAzureConfigured, forwardMode]);

  useEffect(() => {
    loadEmail();
  }, [id]);

  async function loadEmail() {
    setLoading(true);
    setError(null);

    // Check cache first
    const cacheKey = blobPath
      ? { container: blobContainer, blobPath }
      : { id };

    const cachedEmail = getFromCache(cacheKey);
    if (cachedEmail) {
      setEmail(cachedEmail);
      setShowHtml(!!cachedEmail.body_html);
      setLoading(false);
      return;
    }

    try {
      const data = blobPath
        ? await getBlobEmail({ container: blobContainer, path: blobPath })
        : await getEmail(id);

      // Add to cache for future access
      addToCache(cacheKey, data);

      setEmail(data);
      // Default to HTML if available, else plain text
      setShowHtml(!!data.body_html);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // Handle forward to Outlook via Graph API
  const handleForwardOutlook = useCallback(async () => {
    if (!email || forwarding) return;

    setForwarding(true);
    setForwardError(null);
    setForwardSuccess(null);

    try {
      // Get access token (will prompt for login if needed)
      const accessToken = await getAccessToken();

      // Create draft in Outlook (pass blob info if viewing from blob storage)
      const result = await createOutlookDraft(email.id, accessToken, blobInfo);

      // Show success message
      let message = 'Draft created in Outlook!';
      if (result.attachmentsUploaded > 0) {
        message += ` ${result.attachmentsUploaded} attachment(s) included.`;
      }
      if (result.attachmentsFailed > 0) {
        message += ` ${result.attachmentsFailed} attachment(s) could not be uploaded.`;
      }
      setForwardSuccess(message);

      // Open draft in Outlook
      if (result.webLink) {
        window.open(result.webLink, '_blank');
      }
    } catch (err) {
      console.error('Failed to create Outlook draft:', err);
      if (err.code === 'user_cancelled') {
        // User cancelled login, don't show error
        return;
      }
      setForwardError(err.message || 'Failed to create draft. Please try again.');
    } finally {
      setForwarding(false);
    }
  }, [email, forwarding, getAccessToken, blobInfo]);

  // Handle forward as EML file
  const handleForwardEml = useCallback(async () => {
    if (!email || forwarding) return;

    setForwarding(true);
    setForwardError(null);
    setForwardSuccess(null);

    try {
      await forwardEmail(email.id, blobInfo);
      setForwardSuccess('EML file downloaded. Open it with your email client.');
    } catch (err) {
      console.error('Failed to download EML:', err);
      setForwardError('Failed to generate EML file. Please try again.');
    } finally {
      setForwarding(false);
    }
  }, [email, forwarding, blobInfo]);

  // Handle forward action based on selected mode
  const handleForward = useCallback(async () => {
    if (forwardMode === 'outlook') {
      await handleForwardOutlook();
    } else {
      await handleForwardEml();
    }
  }, [forwardMode, handleForwardOutlook, handleForwardEml]);

  // Handle attachment download
  const handleDownloadAttachment = useCallback(async (attachment) => {
    setDownloadError(null);
    setDownloadingAttachment(attachment.name);
    try {
      // Pass the attachment's blob path if available from email metadata
      await downloadAttachment(email.id, attachment.name, blobInfo, attachment.blobPath);
    } catch (err) {
      console.error('Failed to download attachment:', err);
      setDownloadError(`Failed to download "${attachment.name}": ${err.message}`);
    } finally {
      setDownloadingAttachment(null);
    }
  }, [email, blobInfo]);


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700 font-medium">Failed to load email</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          Go back
        </button>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="bg-gray-50 rounded-lg p-8 text-center">
        <p className="text-gray-500">Email not found</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button and Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* Forward Controls */}
        <div className="flex items-center space-x-2">
          {/* Forward Mode Selector */}
          <select
            value={forwardMode}
            onChange={(e) => setForwardMode(e.target.value)}
            disabled={forwarding}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {isAzureConfigured && (
              <option value="outlook">to Outlook Draft</option>
            )}
            <option value="eml">as EML file</option>
          </select>

          {/* Forward Button */}
          <button
            onClick={handleForward}
            disabled={forwarding}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Forward email"
          >
            {forwarding && (
              <svg className="w-5 h-5 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {forwarding ? 'Processing...' : 'Forward'}
          </button>
        </div>
      </div>

      {/* Forward Status Messages */}
      {forwardError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <svg className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-red-700 font-medium">Forward failed</p>
            <p className="text-red-600 text-sm mt-1">{forwardError}</p>
          </div>
          <button
            onClick={() => setForwardError(null)}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {forwardSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
          <svg className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-green-700 font-medium">Success</p>
            <p className="text-green-600 text-sm mt-1">{forwardSuccess}</p>
          </div>
          <button
            onClick={() => setForwardSuccess(null)}
            className="ml-auto text-green-400 hover:text-green-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Email Header */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-xl font-semibold text-gray-900 mb-4">{email.subject || '(No Subject)'}</h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            {/* From */}
            {(email.sender_name || email.sender_email) && (
              <div>
                <span className="text-gray-500">From:</span>
                <div className="font-medium text-gray-900">
                  {email.sender_name && <span>{email.sender_name} </span>}
                  {email.sender_email && (
                    <span className="text-gray-600">&lt;{email.sender_email}&gt;</span>
                  )}
                </div>
              </div>
            )}

            {/* Date */}
            <div>
              <span className="text-gray-500">Date:</span>
              <div className="font-medium text-gray-900">
                {email.date ? formatDate(email.date) : 'Unknown'}
              </div>
            </div>

            {/* To */}
            {email.recipients?.to?.length > 0 && (
              <div className="md:col-span-2">
                <span className="text-gray-500">To:</span>
                <div className="text-gray-900">
                  {email.recipients.to.map((r, i) => (
                    <span key={i}>
                      {i > 0 && ', '}
                      {r.name && <span>{r.name} </span>}
                      <span className="text-gray-600">&lt;{r.email}&gt;</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* CC */}
            {email.recipients?.cc?.length > 0 && (
              <div className="md:col-span-2">
                <span className="text-gray-500">CC:</span>
                <div className="text-gray-900">
                  {email.recipients.cc.map((r, i) => (
                    <span key={i}>
                      {i > 0 && ', '}
                      {r.name && <span>{r.name} </span>}
                      <span className="text-gray-600">&lt;{r.email}&gt;</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Folder */}
            {email.folder && (
              <div>
                <span className="text-gray-500">Folder:</span>
                <div className="text-gray-900">{email.folder}</div>
              </div>
            )}

            {/* Importance */}
            {email.importance && email.importance !== 'normal' && (
              <div>
                <span className="text-gray-500">Importance:</span>
                <div className={`font-medium ${email.importance === 'high' ? 'text-red-600' : 'text-gray-900'}`}>
                  {email.importance}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Attachments */}
        {email.attachments?.length > 0 && (
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center text-sm text-gray-600 mb-2">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              {email.attachments.length} Attachment{email.attachments.length > 1 ? 's' : ''}
            </div>
            {downloadError && (
              <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm flex items-center justify-between">
                <span>{downloadError}</span>
                <button onClick={() => setDownloadError(null)} className="text-red-400 hover:text-red-600 ml-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {email.attachments.map((att, i) => (
                <button
                  key={i}
                  onClick={() => handleDownloadAttachment(att)}
                  disabled={downloadingAttachment === att.name}
                  className="inline-flex items-center px-3 py-1 bg-white border border-gray-200 rounded-full text-sm hover:bg-blue-50 hover:border-blue-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                  title={`Download ${att.name}`}
                >
                  {downloadingAttachment === att.name ? (
                    <svg className="w-4 h-4 mr-2 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  )}
                  {att.name}
                  {att.size && <span className="ml-2 text-gray-400">({formatFileSize(att.size)})</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Body */}
        <div className="p-6">
          {/* Toggle HTML/Text */}
          {email.body_html && email.body_text && (
            <div className="flex items-center space-x-2 mb-4 text-sm">
              <span className="text-gray-500">View:</span>
              <button
                onClick={() => setShowHtml(true)}
                className={`px-3 py-1 rounded ${showHtml ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                HTML
              </button>
              <button
                onClick={() => setShowHtml(false)}
                className={`px-3 py-1 rounded ${!showHtml ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Plain Text
              </button>
            </div>
          )}

          {/* Email Content */}
          {showHtml && email.body_html ? (
            <div
              className="email-body prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(email.body_html) }}
            />
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">
              {email.body_text || '(No content)'}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function sanitizeHtml(html) {
  // Basic sanitization - remove script tags
  // For production, use a proper sanitization library like DOMPurify
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '');
}

export default EmailDetail;
